import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { availableLanguages } from '../../locales';
import ErrorAlert from '../vedic/feedback/ErrorAlert';
import LoadingState from '../vedic/feedback/LoadingState';
import usePanchangamData from '../vedic/panchangam/hooks/usePanchangamData';
import useLiveTime from '../vedic/panchangam/hooks/useLiveTime';
import useThemeMode from '../vedic/panchangam/hooks/useThemeMode';
import VedicThemeProvider from '../vedic/theme/VedicThemeProvider';
import PanchangamForm from '../vedic/panchangam/PanchangamForm';
import PanchangamSheet from '../vedic/panchangam/sheet/PanchangamSheet';
import ComparisonView from '../vedic/panchangam/comparison/ComparisonView';
import ThemeToggle from '../vedic/panchangam/ThemeToggle';

const darkMuiTheme = createTheme({ palette: { mode: 'dark' } });

const SIDDHANTA_STORAGE_KEY = 'vedic_todayPanchangam_siddhanta';
const VALID_SIDDHANTA = ['drik', 'vakyam', 'compare'];

function loadInitialSiddhanta(searchParams) {
  const urlVal = searchParams.get('siddhanta');
  if (VALID_SIDDHANTA.includes(urlVal)) return urlVal;

  try {
    const stored = sessionStorage.getItem(SIDDHANTA_STORAGE_KEY);
    if (VALID_SIDDHANTA.includes(stored)) return stored;
  } catch {}

  return undefined; // default-first-load path — no param sent to backend
}

/* ─── Styled Components (kept from #161) ─── */

const PageContainer = styled.div`
  max-width: 1100px;
  margin: 40px auto;
  padding: 0 20px 60px;
  background: ${p => p.theme.colors.background};
  min-height: 100vh;
`;

const LangToggle = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  margin-bottom: 16px;
`;

const LangBtn = styled.button`
  padding: 6px 14px;
  border: 1px solid ${p => p.active ? p.theme.colors.bands.exceptional : p.theme.colors.border};
  background: ${p => p.active ? p.theme.colors.bands.exceptional : p.theme.colors.surface};
  color: ${p => p.active ? p.theme.colors.surface : p.theme.colors.text.secondary};
  border-radius: ${p => p.theme.radius.sm}px;
  font-size: ${p => p.theme.typography.sizes.sm};
  font-weight: ${p => p.active ? p.theme.typography.weights.semibold : p.theme.typography.weights.regular};
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: ${p => p.theme.colors.bands.exceptional}; }
`;

const PageTitle = styled.h1`
  font-size: ${p => p.theme.typography.sizes.xxl};
  text-align: center;
  margin-bottom: 8px;
  color: ${p => p.theme.colors.text.primary};
`;

const PageSubtitle = styled.p`
  text-align: center;
  color: ${p => p.theme.colors.text.secondary};
  margin-bottom: 10px;
  font-size: ${p => p.theme.typography.sizes.md};
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.bands.exceptional};
  font-size: ${p => p.theme.typography.sizes.md};
  cursor: pointer;
  margin-bottom: 20px;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

/* ─── Main Component ─── */

const TodayPanchangam = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang, setLang] = useState('en');
  const { mode: themeMode, resolved, setMode: setThemeMode } = useThemeMode();

  // Form state
  const [place, setPlace] = useState('Chennai, Tamil Nadu, India');
  const [lat, setLat] = useState(13.0827);
  const [lon, setLon] = useState(80.2707);
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [ayanamsa, setAyanamsa] = useState(() =>
    localStorage.getItem('vedic_panchangam_ayanamsa') || 'lahiri'
  );

  // Siddhanta state — URL > sessionStorage > undefined (default first load)
  const [siddhanta, setSiddhantaState] = useState(() => loadInitialSiddhanta(searchParams));

  const setSiddhanta = useCallback((next) => {
    setSiddhantaState(next);

    try {
      if (next) {
        sessionStorage.setItem(SIDDHANTA_STORAGE_KEY, next);
      } else {
        sessionStorage.removeItem(SIDDHANTA_STORAGE_KEY);
      }
    } catch {}

    const newParams = new URLSearchParams(searchParams);
    if (next) newParams.set('siddhanta', next);
    else newParams.delete('siddhanta');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Data hook
  const { mode: dataMode, data, loading, error, refetch } = usePanchangamData({
    lat, lon, timezone, date, ayanamsa, siddhanta,
  });

  // Live time for countdowns
  const { now } = useLiveTime();

  const ayanamsaDisabled = siddhanta === 'vakyam' || siddhanta === 'compare';

  const handlePlaceChange = useCallback((d) => {
    setPlace(d.place);
    setLat(d.lat);
    setLon(d.lon);
    setTimezone(d.timezone);
  }, []);

  const panchangamTitle = lang === 'ta' ? 'இன்றைய பஞ்சாங்கம்' : "Today's Panchangam";
  const panchangamSub = lang === 'ta'
    ? 'நேரங்கள் மற்றும் சுப காலங்களுடன் தினசரி பஞ்சாங்கம்'
    : 'Daily Panchanga with timings and auspicious periods';

  const formElement = (
    <PanchangamForm
      place={place}
      lat={lat}
      lon={lon}
      timezone={timezone}
      date={date}
      ayanamsa={ayanamsa}
      siddhanta={siddhanta || 'drik'}
      onPlaceChange={handlePlaceChange}
      onDateChange={setDate}
      onAyanamsaChange={setAyanamsa}
      onSiddhantaChange={setSiddhanta}
      ayanamsaDisabled={ayanamsaDisabled}
      loading={loading}
      lang={lang}
    />
  );

  return (
    <VedicThemeProvider mode={resolved}>
      <PageContainer>
        <LangToggle>
          {availableLanguages.map(l => (
            <LangBtn key={l.code} active={lang === l.code} onClick={() => setLang(l.code)}>
              {l.label}
            </LangBtn>
          ))}
          <ThemeToggle mode={themeMode} onChange={setThemeMode} />
        </LangToggle>

        <BackLink onClick={() => navigate('/free-kundli')}>
          &larr; {lang === 'ta' ? 'ஜாதகத்திற்கு திரும்பு' : 'Back to Free Kundli'}
        </BackLink>

        <PageTitle>{panchangamTitle}</PageTitle>
        <PageSubtitle>{panchangamSub}</PageSubtitle>

        {resolved === 'dark' ? (
          <MuiThemeProvider theme={darkMuiTheme}>
            {formElement}
          </MuiThemeProvider>
        ) : formElement}

        <ErrorAlert message={error} onDismiss={() => refetch()} />

        <LoadingState
          loading={loading && !data}
          variant="table"
          message={lang === 'ta' ? 'பஞ்சாங்கம் ஏற்றுகிறது...' : 'Loading panchangam...'}
        />

        {data && dataMode === 'single' && (
          <PanchangamSheet
            dailyPanchang={data}
            now={now}
            lang={lang}
            siddhanta={siddhanta}
          />
        )}

        {data && dataMode === 'compare' && (
          <VedicThemeProvider mode="light">
            <ComparisonView result={data} now={now} lang={lang} />
          </VedicThemeProvider>
        )}
      </PageContainer>
    </VedicThemeProvider>
  );
};

export default TodayPanchangam;
